import { Box, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { useMemo } from 'react';
import Radio from '@/components/shared/Radio';
import { SearchBox } from '@/components/shared/SearchBox';
import useOrchestrateAppointmentFlow from '@/hooks/useOrchestrateAppointmentFlow';
import { getZipCode } from '@/lib/tagging';
import useAppointmentStore, {
  isInBranchAppointment as isInBranchAppointmentSelector,
  isOnlineAppointment as isOnlineAppointmentSelector,
} from '@/store/AppointmentStore';
import type { Branch } from '../utils/Branch';

type AppointmentTypeSelectionProps = {
  branchSearchTerm: string;
  setBranchSearchTerm: (value: string) => void;
  branchesData?: Branch[];
  isLoading?: boolean;
  zipCodeError?: string;
};

const AppointmentTypeSelection: React.FC<AppointmentTypeSelectionProps> = ({
  branchSearchTerm,
  setBranchSearchTerm,
  branchesData,
  isLoading = false,
  zipCodeError,
}) => {
  const preSelectedZipCode = useAppointmentStore(
    (state) => state.preSelectedZipCode,
  );
  const selectedBranch = useAppointmentStore((state) => state.selectedBranch);
  const setSelectedBranch = useAppointmentStore(
    (state) => state.setSelectedBranch,
  );
  const isInBranchAppointment = useAppointmentStore(
    isInBranchAppointmentSelector,
  );
  const isOnlineAppointment = useAppointmentStore(isOnlineAppointmentSelector);

  const orchestrateAppointmentFlow = useOrchestrateAppointmentFlow();

  const zipCode = useMemo(
    () => preSelectedZipCode || getZipCode(),
    [preSelectedZipCode],
  );

  const handleSearchChange = (search: string) => {
    setBranchSearchTerm(search);
  };

  const handleClearingSearchResults = () => {
    setSelectedBranch(null);
    setBranchSearchTerm('');
  };

  const displayError =
    zipCodeError ||
    (branchSearchTerm?.match(/^\d{5}$/) && !isLoading && !branchesData?.length
      ? 'The ZIP code entered is outside of the service area.'
      : '');

  return (
    <Box
      sx={{
        width: '100%',
        p: 2,
      }}
      data-testid='appointmentTypeContainer'
    >
      <Stack
        direction='column'
        spacing={2}
        sx={{
          backgroundColor: 'background.default',
          p: 2,
          borderRadius: 2,
        }}
      >
        <Typography
          variant='h5'
          sx={{
            width: '100%',
            textAlign: 'left',
          }}
        >
          How do you want to meet?
        </Typography>
        <Stack
          direction='row'
          sx={{
            gap: {
              xs: '9px',
              sm: '108px',
            },
            '& .MuiFormControlLabel-root': {
              border: 'none !important',
              fontSize: '16px',
              fontWeight: 500,
              textAlign: 'left',
              pl: '1px !important',
            },
          }}
        >
          <Radio
            label='Phone'
            value='online'
            checked={isOnlineAppointment}
            onClick={() => orchestrateAppointmentFlow('online')}
          />
          <Radio
            label='In-person'
            value='in-branch'
            checked={isInBranchAppointment}
            onClick={() => orchestrateAppointmentFlow('in-branch')}
          />
        </Stack>
        {isInBranchAppointment && (
          <Stack direction='column' spacing={0.5}>
            <Typography
              variant='view-label-primary'
              sx={{
                textAlign: 'left',
              }}
            >
              Type in your preferred branch or zip code.
            </Typography>
            <SearchBox
              placeholder={zipCode || 'Search by branch name or zip code'}
              value={branchSearchTerm}
              selectedResult={
                selectedBranch
                  ? {
                      id: selectedBranch.branchId,
                      title: selectedBranch.branchName,
                      subtitle: selectedBranch.branchAddress,
                    }
                  : null
              }
              onChange={handleSearchChange}
              onClearSelection={handleClearingSearchResults}
              isLoading={isLoading}
              error={displayError}
              hideSearchIconOnSearch
              simpleSelectedResultView
            />
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default AppointmentTypeSelection;
